import { useState, useEffect, useRef } from 'react';
import liff from '@line/liff';

// Local dev only: bypass real LINE login. The "ID token" is just the selected
// dev user's LINE id (see the dev user picker), which the API's AUTH_BYPASS
// path treats as the user `sub`.
const DEV_AUTH = process.env.REACT_APP_DEV_AUTH === '1';
export const getDevLineUserId = () =>
  (typeof window !== 'undefined' && window.localStorage.getItem('devLineUserId')) || 'Udev_customer';

// liff.init() MUST run exactly once per page load. Several components call
// useLiff() (UserContext, UserHeader, CourtDetails, UserProfile, StaffDashboard),
// so without this they each fire their own liff.init() concurrently — and on the
// login redirect they race to exchange the SAME one-time OAuth `code`; the loser
// gets "invalid authorization code" (400), which left the header stuck loading.
// A single shared promise makes every caller await the ONE init.
let liffInitPromise = null;
const initLiffOnce = () => {
  if (!liffInitPromise) {
    liffInitPromise = liff.init({ liffId: process.env.REACT_APP_LIFF_ID });
  }
  return liffInitPromise;
};

export const useLiff = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const initializationRef = useRef(false);

  useEffect(() => {
    const initializeLiff = async () => {
      // Prevent multiple simultaneous initializations within the same component instance
      if (initializationRef.current) {
        console.log('🔍 useLiff: Already initializing in this instance');
        return;
      }

      try {
        initializationRef.current = true;
        setIsLoading(true);
        setError(null);

        // Dev bypass: skip real LIFF init entirely.
        if (DEV_AUTH) {
          console.log('🧪 useLiff: DEV_AUTH on — bypassing LINE login as', getDevLineUserId());
          setIsInitialized(true);
          setIsLoading(false);
          return;
        }

        console.log('🔍 useLiff: Starting LIFF initialization...');

        // Shared single init across ALL useLiff() callers (see initLiffOnce).
        await initLiffOnce();
        setIsInitialized(true);
        console.log('🔍 useLiff: LIFF initialized successfully');
        
      } catch (err) {
        console.error('🔍 useLiff: Failed to initialize LIFF:', err);
        setError(err.message);
        setIsInitialized(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeLiff();
  }, []);

  // Dev bypass: a fake LIFF whose token is the selected dev user's LINE id.
  const devLiff = {
    isLoggedIn: () => true,
    isInClient: () => false,
    getIDToken: () => getDevLineUserId(),
    login: async () => {},
    logout: async () => {},
    getProfile: async () => ({
      userId: getDevLineUserId(),
      displayName: getDevLineUserId().replace(/^U/, '').replace(/[_-]+/g, ' '),
      pictureUrl: '',
    }),
  };

  // Safe wrapper functions that check if LIFF is initialized
  const safeLiff = DEV_AUTH ? devLiff : {
    isLoggedIn: () => isInitialized ? liff.isLoggedIn() : false,
    isInClient: () => isInitialized ? liff.isInClient() : false,
    getIDToken: () => isInitialized ? liff.getIDToken() : null,
    login: async () => {
      if (!isInitialized) {
        throw new Error('LIFF not initialized');
      }
      return await liff.login();
    },
    logout: async () => {
      if (!isInitialized) {
        throw new Error('LIFF not initialized');
      }
      return await liff.logout();
    },
    getProfile: async () => {
      if (!isInitialized) {
        throw new Error('LIFF not initialized');
      }
      return await liff.getProfile();
    }
  };

  return {
    liff: safeLiff,
    isInitialized,
    isLoading,
    error
  };
}; 